import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function ClientNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/notifications').then(r => { setNotifications(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    toast.success('All notifications marked as read');
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Notifications</h1>
          <p className="text-sm text-[#5A5A5A] mt-1">{unreadCount} unread</p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" className="rounded-full text-sm" onClick={markAllRead} data-testid="mark-all-read-btn">
            <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Card className="rounded-2xl border-[#E5E5E0] border-dashed bg-white"><CardContent className="p-8 text-center"><Bell className="w-10 h-10 text-[#D6D6D0] mx-auto mb-3" /><p className="text-sm text-[#5A5A5A]">No notifications</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {notifications.map(n => (
            <Card key={n.notification_id} className={`rounded-2xl border-[#E5E5E0] ${n.read ? 'bg-white' : 'bg-[#FFFDF5]'}`} data-testid={`notif-${n.notification_id}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${n.read ? 'bg-transparent' : 'bg-[#E07A5F]'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#2D2D2D]">{n.title}</p>
                  <p className="text-sm text-[#5A5A5A]">{n.message}</p>
                  <p className="text-xs text-[#888] mt-1">{n.created_at?.split('T')[0]}</p>
                </div>
                {!n.read && (
                  <Button variant="ghost" size="sm" onClick={() => markRead(n.notification_id)} className="text-xs text-[#888]" data-testid={`mark-read-${n.notification_id}`}>
                    <Check className="w-3 h-3" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
